import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import VendorCodeModal, { getStoredVendorCode, getStoredVendorInfo, VendorCodeBadge } from '@/components/VendorCodeModal';
import { toast } from 'sonner';
import { 
  MapPin, Clock, Shield, Lock, AlertTriangle, 
  ArrowLeft, CreditCard, CheckCircle, Users, FileText,
  Calendar, DollarSign, Info
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CheckoutPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const vendorSlug = searchParams.get('v');

  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1); // 1: Info, 2: Términos, 3: Firma
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [requirementsAccepted, setRequirementsAccepted] = useState(false);
  const [presentationAccepted, setPresentationAccepted] = useState(false);
  
  // Vendor code modal
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [vendorCode, setVendorCode] = useState(getStoredVendorCode());
  const [vendorInfo, setVendorInfo] = useState(getStoredVendorInfo());

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    // Datos adicionales para parejas
    partnerName: '',
    relationshipYears: '',
    combinedIncome: ''
  });

  // Check for vendor code on mount
  useEffect(() => {
    const storedCode = getStoredVendorCode();
    const storedInfo = getStoredVendorInfo();
    
    if (storedCode) {
      setVendorCode(storedCode);
      setVendorInfo(storedInfo);
    } else if (!vendorSlug) {
      // No vendor code and not coming from vendor page - show modal
      setShowVendorModal(true);
    }
  }, [vendorSlug]);

  useEffect(() => {
    const fetchCertificate = async () => {
      try {
        const response = await fetch(`${API}/certificates/${id}`);
        if (response.ok) {
          const data = await response.json();
          setCertificate(data);
        }
      } catch (error) {
        console.error('Error fetching certificate:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCertificate();
  }, [id]);

  // Track abandoned cart when user enters email
  useEffect(() => {
    const trackAbandonedCart = async () => {
      if (formData.email && certificate && formData.email.includes('@')) {
        try {
          await fetch(`${API}/checkout/abandoned-cart`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              certificate_id: id,
              customer_email: formData.email,
              customer_name: formData.name || null,
              customer_phone: formData.phone || null,
              vendor_code: vendorCode || getStoredVendorCode(),
              vendor_slug: vendorSlug
            })
          });
        } catch (error) {
          console.log('Abandoned cart tracking failed:', error);
        }
      }
    };
    
    // Debounce the tracking
    const timeout = setTimeout(trackAbandonedCart, 2000);
    return () => clearTimeout(timeout);
  }, [formData.email, formData.name, certificate, id, vendorCode, vendorSlug]);

  // Notify vendor about potential abandoned cart when user leaves
  useEffect(() => {
    const handleBeforeUnload = async () => {
      if (formData.email && certificate && !submitting) {
        // Use sendBeacon for reliable delivery on page unload
        const data = JSON.stringify({
          certificate_id: id,
          customer_email: formData.email,
          customer_name: formData.name,
          customer_phone: formData.phone,
          vendor_code: vendorCode || getStoredVendorCode(),
          vendor_slug: vendorSlug
        });
        navigator.sendBeacon(`${API}/checkout/notify-vendor-abandoned`, data);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [formData, certificate, id, vendorCode, vendorSlug, submitting]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check for vendor code
    const currentVendorCode = vendorCode || getStoredVendorCode();
    if (!currentVendorCode && !vendorSlug) {
      setShowVendorModal(true);
      toast.error('Necesitas un código de vendedor para comprar');
      return;
    }
    
    if (!termsAccepted || !requirementsAccepted || !presentationAccepted) {
      toast.error('Debes aceptar todos los términos y condiciones');
      return;
    }

    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }

    // Validar datos de pareja si es certificado para parejas
    if (certificate?.certificate_type === 'couple') {
      if (!formData.partnerName) {
        toast.error('Por favor ingresa el nombre de tu pareja');
        return;
      }
    }

    setSubmitting(true);

    try {
      const response = await fetch(`${API}/checkout/create-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          certificate_id: id,
          vendor_slug: vendorSlug,
          vendor_code: currentVendorCode,
          customer: {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            address: formData.address
          },
          terms_accepted: termsAccepted,
          origin_url: window.location.origin
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al procesar el pago');
      }

      const data = await response.json();
      window.location.href = data.checkout_url;
    } catch (error) {
      toast.error(error.message);
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="flex justify-center items-center py-40">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  if (!certificate) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-20 text-center">
          <h1 className="font-heading text-2xl font-bold text-slate-900 mb-4">
            Certificado no encontrado
          </h1>
          <Button onClick={() => navigate('/certificados')}>Ver todos los certificados</Button>
        </div>
        <Footer />
      </div>
    );
  }

  const isCoupleCertificate = certificate.certificate_type === 'couple';

  return (
    <div className="min-h-screen bg-slate-50" data-testid="checkout-page">
      <Navbar />
      
      {/* Vendor Code Modal */}
      <VendorCodeModal 
        isOpen={showVendorModal} 
        onClose={() => setShowVendorModal(false)}
        onSuccess={(result) => {
          setVendorCode(result.vendor_code);
          setVendorInfo({
            name: result.vendor_name,
            slug: result.vendor_slug,
            id: result.vendor_id
          });
        }}
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Vendor Code Badge */}
        {(vendorCode || vendorSlug) && (
          <VendorCodeBadge className="mb-6" />
        )}
        
        {/* Breadcrumb */}
        <button 
          onClick={() => navigate(-1)} 
          className="inline-flex items-center text-sm text-slate-600 hover:text-teal-600 mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver
        </button>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 mb-8">
          {[
            { num: 1, label: 'Información' },
            { num: 2, label: 'Términos' },
            { num: 3, label: 'Firma y Pago' }
          ].map((s, idx) => (
            <div key={s.num} className="flex items-center">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full font-bold text-sm
                ${step >= s.num ? 'bg-teal-500 text-white' : 'bg-slate-200 text-slate-500'}`}>
                {step > s.num ? <CheckCircle className="w-5 h-5" /> : s.num}
              </div>
              <span className={`ml-2 text-sm font-medium hidden sm:block
                ${step >= s.num ? 'text-teal-600' : 'text-slate-400'}`}>
                {s.label}
              </span>
              {idx < 2 && <div className={`w-12 h-0.5 mx-3 ${step > s.num ? 'bg-teal-500' : 'bg-slate-200'}`} />}
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2">
            {/* Step 1: Customer Information */}
            {step === 1 && (
              <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm">
                <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                  Información del Comprador
                </h1>
                <p className="text-slate-500 mb-6">
                  {isCoupleCertificate 
                    ? 'Este certificado es para 2 personas (pareja). Completa los datos de ambos.'
                    : 'Completa tus datos para continuar con la reserva.'}
                </p>

                {/* Alert for couples */}
                {isCoupleCertificate && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                    <div className="flex items-start gap-3">
                      <Users className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="font-semibold text-amber-800 text-sm mb-1">
                          Certificado para Parejas
                        </h3>
                        <p className="text-amber-700 text-sm">
                          {certificate.couple_requirements}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <form className="space-y-6">
                  {/* Primary guest */}
                  <div>
                    <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-teal-100 text-teal-600 flex items-center justify-center text-sm">1</span>
                      {isCoupleCertificate ? 'Titular del Certificado' : 'Datos del Viajero'}
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name">Nombre completo *</Label>
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="Tu nombre completo"
                          className="mt-1.5"
                          required
                          data-testid="input-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email">Correo electrónico *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="tu@email.com"
                          className="mt-1.5"
                          required
                          data-testid="input-email"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone">Teléfono *</Label>
                        <Input
                          id="phone"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="+1 234 567 8900"
                          className="mt-1.5"
                          required
                          data-testid="input-phone"
                        />
                      </div>
                      <div>
                        <Label htmlFor="address">Dirección</Label>
                        <Input
                          id="address"
                          value={formData.address}
                          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                          placeholder="Tu dirección"
                          className="mt-1.5"
                          data-testid="input-address"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Partner info for couples */}
                  {isCoupleCertificate && (
                    <div className="pt-6 border-t border-slate-200">
                      <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center text-sm">2</span>
                        Datos de la Pareja
                      </h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="partnerName">Nombre completo de tu pareja *</Label>
                          <Input
                            id="partnerName"
                            value={formData.partnerName}
                            onChange={(e) => setFormData({ ...formData, partnerName: e.target.value })}
                            placeholder="Nombre de tu pareja"
                            className="mt-1.5"
                            required
                            data-testid="input-partner-name"
                          />
                        </div>
                        <div>
                          <Label htmlFor="relationshipYears">Años de relación/matrimonio</Label>
                          <Input
                            id="relationshipYears"
                            value={formData.relationshipYears}
                            onChange={(e) => setFormData({ ...formData, relationshipYears: e.target.value })}
                            placeholder="Ej: 3 años"
                            className="mt-1.5"
                            data-testid="input-relationship"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <Button
                    type="button"
                    onClick={() => {
                      if (!formData.name || !formData.email || !formData.phone) {
                        toast.error('Completa los campos requeridos');
                        return;
                      }
                      if (isCoupleCertificate && !formData.partnerName) {
                        toast.error('Ingresa el nombre de tu pareja');
                        return;
                      }
                      setStep(2);
                    }}
                    className="w-full bg-teal-500 hover:bg-teal-600 text-white rounded-xl py-6"
                    data-testid="continue-to-terms"
                  >
                    Continuar a Términos y Condiciones
                  </Button>
                </form>
              </div>
            )}

            {/* Step 2: Terms and Conditions */}
            {step === 2 && (
              <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm">
                <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                  Términos y Condiciones
                </h1>
                <p className="text-slate-500 mb-6">
                  Lee cuidadosamente los términos antes de continuar. Tu aceptación constituye un acuerdo legal.
                </p>

                {/* Important notice about presentation */}
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-5 mb-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <AlertTriangle className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-amber-800 mb-2">
                        CONDICIÓN IMPORTANTE - LEA CON ATENCIÓN
                      </h3>
                      <p className="text-amber-700 text-sm leading-relaxed">
                        {certificate.presentation_info}
                      </p>
                      <div className="mt-3 p-3 bg-white/50 rounded-lg">
                        <p className="text-amber-800 text-sm font-medium">
                          ¿Por qué el precio es tan bajo? El extraordinario descuento (hasta 70%) es posible gracias a que 
                          usted dedica aproximadamente 2 horas de su estadía a conocer las opciones de membresías vacacionales 
                          del resort. Esta presentación NO implica obligación de compra.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Full Terms */}
                <div className="border border-slate-200 rounded-xl mb-6">
                  <div className="p-4 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-slate-600" />
                    <h3 className="font-semibold text-slate-800">Documento Legal - Términos Completos</h3>
                  </div>
                  <div className="p-4 max-h-96 overflow-y-auto">
                    <pre className="text-sm text-slate-600 whitespace-pre-wrap font-sans leading-relaxed">
                      {certificate.terms_conditions}
                    </pre>
                  </div>
                </div>

                {/* Acceptance checkboxes */}
                <div className="space-y-4 mb-6">
                  <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl">
                    <Checkbox
                      id="requirements"
                      checked={requirementsAccepted}
                      onCheckedChange={setRequirementsAccepted}
                      className="mt-1"
                      data-testid="requirements-checkbox"
                    />
                    <Label htmlFor="requirements" className="font-normal text-slate-700 cursor-pointer text-sm leading-relaxed">
                      <strong>Declaro bajo juramento</strong> que cumplo con TODOS los requisitos de elegibilidad mencionados 
                      en los términos y condiciones{isCoupleCertificate && ', incluyendo estar casado(a) o en unión conviviente'}.
                    </Label>
                  </div>

                  <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-xl border border-amber-200">
                    <Checkbox
                      id="presentation"
                      checked={presentationAccepted}
                      onCheckedChange={setPresentationAccepted}
                      className="mt-1"
                      data-testid="presentation-checkbox"
                    />
                    <Label htmlFor="presentation" className="font-normal text-amber-800 cursor-pointer text-sm leading-relaxed">
                      <strong>Acepto y entiendo</strong> que como condición OBLIGATORIA para acceder al precio promocional, 
                      {isCoupleCertificate ? ' ambos miembros de la pareja debemos asistir juntos' : ' debo asistir'} a una 
                      presentación informativa de aproximadamente 90-120 minutos sobre programas vacacionales durante mi estadía. 
                      Entiendo que la no asistencia resultará en el cargo del precio regular.
                    </Label>
                  </div>

                  <div className="flex items-start gap-3 p-4 bg-teal-50 rounded-xl border border-teal-200">
                    <Checkbox
                      id="terms"
                      checked={termsAccepted}
                      onCheckedChange={setTermsAccepted}
                      className="mt-1"
                      data-testid="terms-checkbox"
                    />
                    <Label htmlFor="terms" className="font-normal text-teal-800 cursor-pointer text-sm leading-relaxed">
                      <strong>He leído y acepto</strong> la totalidad de los términos y condiciones del certificado de viaje, 
                      incluyendo la política de no reembolso y todas las condiciones establecidas en el documento anterior.
                    </Label>
                  </div>
                </div>

                <div className="flex gap-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(1)}
                    className="flex-1"
                  >
                    Volver
                  </Button>
                  <Button
                    type="button"
                    onClick={() => {
                      if (!requirementsAccepted || !presentationAccepted || !termsAccepted) {
                        toast.error('Debes aceptar todos los términos para continuar');
                        return;
                      }
                      setStep(3);
                    }}
                    className="flex-1 bg-teal-500 hover:bg-teal-600 text-white"
                    disabled={!requirementsAccepted || !presentationAccepted || !termsAccepted}
                    data-testid="continue-to-sign"
                  >
                    Continuar a Firma
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Signature and Payment */}
            {step === 3 && (
              <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm">
                <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                  Firma Digital y Pago
                </h1>
                <p className="text-slate-500 mb-6">
                  Tu firma digital confirma la aceptación de todos los términos.
                </p>

                {/* Summary of accepted terms */}
                <div className="bg-slate-50 rounded-xl p-5 mb-6">
                  <h3 className="font-semibold text-slate-800 mb-4">Resumen de tu Acuerdo</h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                      <span className="text-sm text-slate-600">
                        Cumples con los requisitos de elegibilidad del certificado
                      </span>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                      <span className="text-sm text-slate-600">
                        Aceptas asistir a la presentación informativa de 90-120 minutos
                      </span>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                      <span className="text-sm text-slate-600">
                        Aceptas los términos y condiciones completos del certificado
                      </span>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                      <span className="text-sm text-slate-600">
                        Entiendes que el certificado NO es reembolsable
                      </span>
                    </div>
                  </div>
                </div>

                {/* Digital signature confirmation */}
                <div className="border-2 border-teal-200 bg-teal-50 rounded-xl p-5 mb-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-teal-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-teal-800">Firma Digital Registrada</h3>
                      <p className="text-xs text-teal-600">Documento legalmente vinculante</p>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-lg p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Firmante:</span>
                      <span className="font-medium text-slate-800">{formData.name}</span>
                    </div>
                    {isCoupleCertificate && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Pareja:</span>
                        <span className="font-medium text-slate-800">{formData.partnerName}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Email:</span>
                      <span className="font-medium text-slate-800">{formData.email}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Fecha y hora:</span>
                      <span className="font-medium text-slate-800">{new Date().toLocaleString('es-ES')}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Certificado:</span>
                      <span className="font-medium text-slate-800">{certificate.title}</span>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="flex gap-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setStep(2)}
                      className="flex-1"
                    >
                      Volver
                    </Button>
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="flex-1 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white rounded-xl py-6"
                      data-testid="submit-checkout-btn"
                    >
                      {submitting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          Procesando...
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-5 h-5 mr-2" />
                          Firmar y Pagar ${(certificate.retail_price + 99).toFixed(2)}
                        </>
                      )}
                    </Button>
                  </div>

                  <div className="flex items-center justify-center gap-4 text-xs text-slate-500 mt-4">
                    <span className="flex items-center gap-1">
                      <Lock className="w-3.5 h-3.5" />
                      Pago seguro con Stripe
                    </span>
                    <span className="flex items-center gap-1">
                      <Shield className="w-3.5 h-3.5" />
                      Datos protegidos
                    </span>
                  </div>
                </form>
              </div>
            )}
          </div>

          {/* Order summary sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
              <h2 className="font-heading text-lg font-semibold text-slate-900 mb-4">
                Resumen de Compra
              </h2>

              <div className="flex gap-4 pb-4 border-b border-slate-100">
                <img 
                  src={certificate.images?.[0] || 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e'}
                  alt={certificate.title}
                  className="w-20 h-20 rounded-lg object-cover"
                />
                <div className="flex-1">
                  <span className="text-xs text-teal-600 font-medium flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {certificate.destination}
                  </span>
                  <h3 className="font-medium text-slate-800 text-sm mt-1 line-clamp-2">
                    {certificate.title}
                  </h3>
                </div>
              </div>

              <div className="py-4 space-y-3 border-b border-slate-100">
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-600">{certificate.days} días / {certificate.nights} noches</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Users className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-600">
                    {certificate.guests} personas
                    {isCoupleCertificate && <span className="text-amber-600 ml-1">(Parejas)</span>}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-600">Válido 12 meses</span>
                </div>
              </div>

              {/* Price */}
              <div className="py-4 border-b border-slate-100">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-slate-600">Certificado ({certificate.guests} pers.)</span>
                  <span className="text-slate-800">${certificate.retail_price.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-slate-600 flex items-center gap-1">
                    Fee de Reserva
                    <span className="inline-block w-4 h-4 rounded-full bg-slate-200 text-slate-600 text-xs flex items-center justify-center cursor-help" title="Fee único por gestión y confirmación de tu reserva en el resort">?</span>
                  </span>
                  <span className="text-slate-800">$99.00</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Impuestos</span>
                  <span className="text-slate-800">Incluidos</span>
                </div>
              </div>

              <div className="pt-4">
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-slate-900">Total a Pagar</span>
                  <div className="text-right">
                    <span className="font-heading text-2xl font-bold text-slate-900">
                      ${(certificate.retail_price + 99).toFixed(2)}
                    </span>
                    <p className="text-xs text-slate-500">USD</p>
                  </div>
                </div>
              </div>

              {/* Badge for certificate type */}
              <div className={`mt-4 p-3 rounded-lg text-center text-sm font-medium
                ${isCoupleCertificate 
                  ? 'bg-rose-50 text-rose-700 border border-rose-200' 
                  : 'bg-teal-50 text-teal-700 border border-teal-200'}`}>
                {isCoupleCertificate 
                  ? '💑 Certificado para Parejas'
                  : '🌍 Sin requisito de pareja'}
              </div>

              {vendorSlug && (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <p className="text-xs text-slate-500 text-center">
                    Referido por: <span className="font-medium text-slate-700">{vendorSlug}</span>
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
